/**
 * CONSAI Business App — AI Assistant Page
 * Redesigned: Neon Executive aesthetic per user design prompt
 */
import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Brain, Send, Sparkles, Calendar, Crown, Loader2,
  MessageSquare, Mic, TrendingUp, CreditCard, FileText, Users
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import MobileNav from '../components/MobileNav';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── helpers ─────────────────────────────────────────── */
const fmt = (n) => (n || 0).toLocaleString('en-IN');

/* ── Neon KPI Card ───────────────────────────────────── */
const NeonCard = ({ label, value, sub, color }) => {
  const colors = {
    cyan:   { border: 'rgba(0,245,255,0.25)',  text: '#00F5FF', shadow: '0 0 18px rgba(0,245,255,0.35)' },
    purple: { border: 'rgba(188,19,254,0.25)', text: '#BC13FE', shadow: '0 0 18px rgba(188,19,254,0.35)' },
    emerald:{ border: 'rgba(52,211,153,0.25)', text: '#34D399', shadow: '0 0 18px rgba(52,211,153,0.35)' },
  };
  const c = colors[color] || colors.cyan;
  return (
    <div
      className="bg-[#1a1a1a] p-5 rounded-lg transition-all hover:bg-[#1f1f1f]"
      style={{ border: `1px solid ${c.border}` }}
    >
      <p className="text-xs font-bold tracking-widest uppercase text-[#8A8A93] mb-2">{label}</p>
      <p className="text-3xl font-bold font-mono" style={{ color: c.text, textShadow: c.shadow }}>
        {value}
      </p>
      <p className="text-xs text-[#8A8A93] mt-1">{sub}</p>
    </div>
  );
};

/* ── Chat Message ────────────────────────────────────── */
const ChatMsg = ({ q }) => (
  <div className="space-y-2 py-4 border-b border-[#1a1a1a] last:border-0">
    <div className="flex items-start gap-2">
      <div className="w-6 h-6 rounded-full bg-[#1a1a1a] border border-[rgba(0,245,255,0.2)] flex items-center justify-center shrink-0 mt-0.5">
        <span className="text-[#00F5FF] text-xs font-bold">Q</span>
      </div>
      <p className="text-sm font-medium text-[#F4F4F5]">{q.question}</p>
    </div>
    {q.answer && (
      <div className="flex items-start gap-2 pl-8">
        <div className="w-6 h-6 rounded-full bg-[rgba(0,245,255,0.08)] border border-[rgba(0,245,255,0.2)] flex items-center justify-center shrink-0 mt-0.5">
          <Brain className="h-3 w-3 text-[#00F5FF]" />
        </div>
        <p className="text-sm text-[#9CA3AF] leading-relaxed whitespace-pre-wrap">{q.answer}</p>
      </div>
    )}
  </div>
);

/* ── Main Page ───────────────────────────────────────── */
const AIAssistant = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [remaining, setRemaining] = useState(-1);
  const [maxPerWeek, setMaxPerWeek] = useState(-1);
  const scrollRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sumRes, qRes] = await Promise.all([
          axios.get(`${API_URL}/api/ai/weekly-summary`, { withCredentials: true }),
          axios.get(`${API_URL}/api/ai/questions?per_page=10`, { withCredentials: true }),
        ]);
        setSummary(sumRes.data);
        setQuestions(qRes.data.questions || []);
        setRemaining(qRes.data.remaining_this_week);
        setMaxPerWeek(qRes.data.max_per_week);
      } catch (err) {
        // Summary may not exist yet
      }
      setSummaryLoading(false);
    };
    fetchData();
  }, []);

  const askQuestion = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setAsking(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/ai/ask`,
        { question },
        { withCredentials: true }
      );
      setQuestions([
        { question: res.data.question, answer: res.data.answer, created_at: new Date().toISOString() },
        ...questions,
      ]);
      setRemaining(res.data.remaining_questions);
      setQuestion('');
      setTimeout(() => scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to get answer');
    }
    setAsking(false);
  };

  const bd = summary?.business_data || {};

  const kpiCards = [
    { label: language === 'hi' ? 'साप्ताहिक बिक्री' : 'Weekly Sales',       value: `₹${fmt(bd.week_sales_total)}`, sub: `${bd.week_sales_count || 0} transactions`, color: 'cyan'    },
    { label: language === 'hi' ? 'नए ग्राहक' : 'New Customers',              value: String(bd.new_customers || 0),              sub: `Total base: ${bd.total_customers || 0}`,             color: 'purple'  },
    { label: language === 'hi' ? 'लंबित चालान' : 'Pending Invoices',         value: String(bd.pending_invoices || 0),            sub: 'Action required',                                   color: 'emerald' },
    { label: language === 'hi' ? 'क्रेडिट बकाया' : 'Credit Receivable',      value: `₹${fmt(bd.credit_given)}`,                  sub: 'Pending collection',                                color: 'cyan'    },
  ];

  return (
    <div
      className="flex h-screen w-full overflow-hidden"
      style={{ background: '#0e0e0e' }}
    >
      <Sidebar />

      {/* Main panel */}
      <main className="flex-1 flex flex-col h-full overflow-hidden lg:ml-64">
        {/* ── Sticky Glass Header ── */}
        <header
          className="shrink-0 border-b border-[#1a1a1a] px-4 md:px-6 py-4 flex items-center justify-between"
          style={{ background: 'rgba(14,14,14,0.90)', backdropFilter: 'blur(14px)' }}
        >
          <div className="flex items-center gap-3">
            <Brain className="h-5 w-5 text-[#00F5FF] hidden lg:block" />
            <h1
              className="text-lg font-bold text-[#F4F4F5]"
              data-testid="ai-assistant-title"
            >
              {language === 'hi' ? 'AI सहायक' : 'AI Assistant'}
            </h1>
            <span
              className="px-2 py-0.5 rounded text-[10px] font-bold tracking-widest uppercase border"
              style={{ background: 'rgba(0,245,255,0.08)', borderColor: 'rgba(0,245,255,0.2)', color: '#00F5FF' }}
            >
              AI Analysis
            </span>
          </div>
          {remaining !== -1 && (
            <span
              className="text-xs font-bold tracking-widest uppercase"
              style={{ color: remaining === 0 ? '#BC13FE' : '#00F5FF' }}
              data-testid="questions-remaining"
            >
              {remaining}/{maxPerWeek} {language === 'hi' ? 'प्रश्न शेष' : 'questions left'}
            </span>
          )}
        </header>

        {/* ── Scrollable Content Area ── */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 md:px-6 py-6 space-y-8"
          data-testid="weekly-summary-card"
        >
          {/* Recent Q&A (newest first) */}
          {questions.length > 0 && (
            <div
              className="bg-[#111111] rounded-xl p-6 border"
              style={{ borderColor: 'rgba(255,255,255,0.06)' }}
              data-testid="questions-list"
            >
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="h-4 w-4 text-[#00F5FF]" />
                <p className="text-sm font-bold text-[#F4F4F5] tracking-wide">
                  {language === 'hi' ? 'पिछले प्रश्न' : 'Conversation History'}
                </p>
              </div>
              {questions.map((q, idx) => (
                <ChatMsg key={idx} q={q} data-testid={`question-card-${idx}`} />
              ))}
            </div>
          )}

          {/* ── Weekly Summary Card ── */}
          <div
            className="bg-[#111111] rounded-xl p-6 md:p-8 relative overflow-hidden border"
            style={{ borderColor: 'rgba(255,255,255,0.06)' }}
          >
            {/* Ambient glow blob */}
            <div
              className="absolute top-0 right-0 w-64 h-64 rounded-full pointer-events-none"
              style={{ background: 'rgba(0,245,255,0.04)', filter: 'blur(80px)' }}
            />

            {/* Summary Header */}
            <div className="flex items-center gap-3 mb-8 relative">
              <Calendar className="h-5 w-5 text-[#00F5FF]" />
              <h3 className="text-lg font-bold text-[#F4F4F5]">
                {language === 'hi' ? 'साप्ताहिक सारांश' : 'Weekly Summary'}
              </h3>
              {summary && (
                <span className="text-xs text-[#8A8A93] ml-auto">
                  {summary.week_start} — {summary.week_end}
                </span>
              )}
            </div>

            {summaryLoading ? (
              <div className="flex items-center gap-3 py-8">
                <Loader2 className="h-5 w-5 animate-spin text-[#00F5FF]" />
                <span className="text-sm text-[#8A8A93]">
                  {language === 'hi' ? 'सारांश बना रहे हैं...' : 'Generating AI summary…'}
                </span>
              </div>
            ) : (
              <>
                {/* 1. Key Numbers */}
                <section className="mb-10">
                  <h4 className="text-xs font-bold tracking-widest uppercase text-[#8A8A93] mb-5 flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ background: '#00F5FF', boxShadow: '0 0 8px rgba(0,245,255,0.8)' }}
                    />
                    {language === 'hi' ? '१) प्रमुख आंकड़े' : '1) Key Numbers (At a Glance)'}
                  </h4>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {kpiCards.map((card) => (
                      <NeonCard key={card.label} {...card} />
                    ))}
                  </div>
                </section>

                {/* 2. Trends & Insights */}
                <section className="mb-10">
                  <h4 className="text-xs font-bold tracking-widest uppercase text-[#8A8A93] mb-5 flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ background: '#BC13FE', boxShadow: '0 0 8px rgba(188,19,254,0.8)' }}
                    />
                    {language === 'hi' ? '२) ट्रेंड्स और अंतर्दृष्टि' : '2) Trends & Insights'}
                  </h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div className="bg-[#161616] p-5 rounded-lg border border-[rgba(255,255,255,0.04)]">
                      <h5 className="text-sm font-bold text-[#00F5FF] mb-3 flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" />
                        {language === 'hi' ? 'बिक्री ट्रेंड' : 'Sales Trend'}
                      </h5>
                      <p className="text-sm text-[#9CA3AF] leading-relaxed" data-testid="summary-text">
                        {summary?.summary_text
                          ? summary.summary_text.slice(0, 280)
                          : (language === 'hi'
                              ? 'इस सप्ताह कोई बिक्री नहीं। अपने 3 मौजूदा ग्राहकों से संपर्क करें।'
                              : 'No sales this week. Reach out to your existing customers to restart activity.'
                            )
                        }
                      </p>
                    </div>
                    <div className="bg-[#161616] p-5 rounded-lg border border-[rgba(255,255,255,0.04)]">
                      <h5 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#BC13FE' }}>
                        <CreditCard className="h-4 w-4" />
                        {language === 'hi' ? 'क्रेडिट और कैशफ्लो' : 'Credit & Cashflow'}
                      </h5>
                      <p className="text-sm text-[#9CA3AF] leading-relaxed">
                        {bd.credit_given > 0
                          ? (language === 'hi'
                              ? `₹${fmt(bd.credit_given)} बकाया है। वसूली न होने पर कार्यशील पूंजी प्रभावित होगी।`
                              : `You have ₹${fmt(bd.credit_given)} receivable. Collect soon to maintain working capital.`
                            )
                          : (language === 'hi' ? 'क्रेडिट शेष ठीक है।' : 'Credit balance looks healthy.')
                        }
                      </p>
                    </div>
                  </div>
                </section>

                {/* 3. Recommendations */}
                <section className="mb-10">
                  <h4 className="text-xs font-bold tracking-widest uppercase text-[#8A8A93] mb-5 flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ background: '#34D399', boxShadow: '0 0 8px rgba(52,211,153,0.8)' }}
                    />
                    {language === 'hi' ? '३) शीर्ष सुझाव' : '3) Top Recommendations'}
                  </h4>
                  <div className="space-y-3">
                    <div className="bg-[#1a1a1a] p-5 rounded-lg border border-[rgba(255,255,255,0.04)] relative overflow-hidden">
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#34D399]" />
                      <div className="flex items-center gap-3 mb-2">
                        <Sparkles className="h-4 w-4 text-[#34D399]" />
                        <span className="text-sm font-bold text-[#F4F4F5]">
                          {language === 'hi' ? 'बिक्री जल्दी शुरू करें (प्राथमिकता 1)' : 'Restart Sales Quickly (Priority 1)'}
                        </span>
                      </div>
                      <ul className="text-sm text-[#9CA3AF] list-disc ml-8 space-y-1">
                        <li>
                          {language === 'hi'
                            ? `अपने ${bd.total_customers || 0} मौजूदा ग्राहकों को WhatsApp करें`
                            : `Call/WhatsApp your ${bd.total_customers || 0} existing customers`}
                        </li>
                        <li>
                          {language === 'hi'
                            ? 'एक साप्ताहिक ऑफ़र बनाएं'
                            : 'Create a simple weekly offer (e.g., Free delivery)'}
                        </li>
                      </ul>
                    </div>
                    {bd.pending_invoices > 0 && (
                      <div className="bg-[#1a1a1a] p-5 rounded-lg border border-[rgba(255,255,255,0.04)] relative overflow-hidden">
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#00F5FF]" />
                        <div className="flex items-center gap-3 mb-2">
                          <FileText className="h-4 w-4 text-[#00F5FF]" />
                          <span className="text-sm font-bold text-[#F4F4F5]">
                            {language === 'hi' ? 'बिलिंग अनुशासन ठीक करें (प्राथमिकता 2)' : 'Fix Billing Discipline (Priority 2)'}
                          </span>
                        </div>
                        <p className="text-sm text-[#9CA3AF] ml-8">
                          {language === 'hi'
                            ? 'लंबित चालान तुरंत बंद करें ताकि नियत तारीख स्पष्ट हो।'
                            : 'Close the pending invoice immediately to ensure the due date is clear.'}
                        </p>
                      </div>
                    )}
                  </div>
                </section>

                {/* 4. Credit Recovery Script */}
                {bd.credit_given > 0 && (
                  <section>
                    <h4 className="text-xs font-bold tracking-widest uppercase text-[#8A8A93] mb-4 flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ background: '#00F5FF', boxShadow: '0 0 8px rgba(0,245,255,0.8)' }}
                      />
                      {language === 'hi' ? '४) क्रेडिट रिकवरी स्क्रिप्ट' : '4) Credit Recovery Script'}
                    </h4>
                    <div
                      className="p-5 rounded-lg relative font-mono text-sm italic leading-relaxed"
                      style={{
                        background: '#0a0a0a',
                        border: '1px solid rgba(0,245,255,0.2)',
                        color: 'rgba(0,245,255,0.8)',
                      }}
                    >
                      &ldquo;Hi, payment of{' '}
                      <strong className="underline decoration-[rgba(0,245,255,0.4)]">
                        ₹{fmt(bd.credit_given)}
                      </strong>{' '}
                      is pending. Please pay today via UPI. I will send QR.&rdquo;
                    </div>
                  </section>
                )}
              </>
            )}
          </div>
          {/* bottom padding for mobile nav */}
          <div className="h-4 lg:h-0" />
        </div>

        {/* ── Fixed Chat Input ── */}
        <div className="shrink-0 px-4 md:px-6 py-4 border-t border-[#1a1a1a]" style={{ background: '#0e0e0e' }}>
          <div className="flex items-center gap-2 mb-2.5">
            <Sparkles className="h-3.5 w-3.5 text-[#00F5FF] animate-pulse" />
            <span className="text-[10px] font-bold tracking-widest uppercase text-[#00F5FF]">
              {language === 'hi' ? 'AI क्वेरी इंजन' : 'Intelligent Query Engine'}
            </span>
          </div>
          <form
            onSubmit={askQuestion}
            className="relative flex items-center rounded-xl px-4 py-3 gap-3"
            style={{
              background: '#1a1a1a',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: question ? '0 0 20px rgba(0,245,255,0.12)' : 'none',
              transition: 'box-shadow 0.2s',
            }}
            data-testid="ai-ask-card"
          >
            <input
              className="flex-1 bg-transparent border-none outline-none text-sm text-[#F4F4F5] placeholder-[#8A8A93]"
              placeholder={language === 'hi' ? 'अपने बिज़नेस के बारे में पूछें...' : 'Ask about your business...'}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              disabled={asking || remaining === 0}
              data-testid="ai-question-input"
            />
            <div className="flex items-center gap-3 shrink-0">
              <button
                type="button"
                className="text-[#8A8A93] hover:text-[#00F5FF] transition-colors"
                tabIndex={-1}
              >
                <Mic className="h-5 w-5" />
              </button>
              <button
                type="submit"
                disabled={asking || !question.trim() || remaining === 0}
                className="p-2 rounded-lg text-[#00F5FF] transition-all disabled:opacity-40"
                style={{ background: 'rgba(0,245,255,0.1)' }}
                data-testid="ai-ask-btn"
              >
                {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </form>
          {remaining === 0 && (
            <p className="text-xs text-[#BC13FE] mt-2 flex items-center gap-1" data-testid="limit-reached">
              <Crown className="h-3 w-3" />
              {language === 'hi'
                ? 'साप्ताहिक सीमा पूरी। प्रीमियम अपग्रेड करें।'
                : 'Weekly limit reached. Upgrade to Premium for unlimited questions.'}
            </p>
          )}
        </div>
      </main>

      <MobileNav />
    </div>
  );
};

export default AIAssistant;
